(() => {
  const SELECTOR = 'career-astrology-experience';

  class CareerAstrologyExperience extends HTMLElement {
    constructor() {
      super();
      this.drawer = this.querySelector('[data-career-drawer]');
      this.panel = this.querySelector('[data-career-drawer-panel]');
      this.form = this.querySelector('[data-career-form]');
      this.serviceName = this.querySelector('[data-career-service-name]');
      this.servicePrice = this.querySelector('[data-career-service-price]');
      this.serviceCaption = this.querySelector('[data-career-service-caption]');
      this.error = this.querySelector('[data-career-error]');
      this.submitButton = this.querySelector('[data-career-submit]');
      this.submitLabel = this.submitButton?.querySelector('[data-career-submit-label]');
      this.lastTrigger = null;
      this.activeService = null;
      this.isSubmitting = false;

      this.services = {
        appointment: {
          id: Number(this.dataset.appointmentVariantId || 0),
          title: this.dataset.appointmentTitle || 'Book Appointment',
          price: this.dataset.appointmentPrice || '',
          available: this.dataset.appointmentAvailable === 'true',
          caption: this.dataset.appointmentCaption || 'Personal consultation with an astrology expert'
        },
        voice: {
          id: Number(this.dataset.voiceVariantId || 0),
          title: this.dataset.voiceTitle || 'Take Voice Report',
          price: this.dataset.voicePrice || '',
          available: this.dataset.voiceAvailable === 'true',
          caption: this.dataset.voiceCaption || 'A personalized voice report prepared from your birth details'
        }
      };

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.form?.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    onDocumentClick(event) {
      const readMore = event.target.closest('[data-career-read-more]');
      if (readMore) {
        const targetId = readMore.getAttribute('aria-controls');
        const content = targetId ? document.getElementById(targetId) : null;
        if (content) {
          const expanded = readMore.getAttribute('aria-expanded') === 'true';
          readMore.setAttribute('aria-expanded', String(!expanded));
          content.hidden = expanded;
          const openLabel = readMore.dataset.openLabel || 'Read More';
          const closeLabel = readMore.dataset.closeLabel || 'Show Less';
          readMore.querySelector('[data-career-toggle-label]').textContent = expanded ? openLabel : closeLabel;
        }
        return;
      }

      const trigger = event.target.closest('[data-career-open]');
      if (trigger) {
        const service = trigger.dataset.careerOpen;
        if (this.services[service]) {
          event.preventDefault();
          this.openDrawer(service, trigger);
        }
        return;
      }

      if (event.target.closest('[data-career-close]') && this.drawer && !this.drawer.hidden) {
        event.preventDefault();
        this.closeDrawer();
      }
    }

    onKeydown(event) {
      if (!this.drawer || this.drawer.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openDrawer(serviceKey, trigger) {
      const service = this.services[serviceKey];
      if (!service || !service.id || !service.available || !this.drawer) return;

      this.activeService = serviceKey;
      this.lastTrigger = trigger || document.activeElement;
      this.clearError();

      if (this.serviceName) this.serviceName.textContent = service.title;
      if (this.servicePrice) this.servicePrice.textContent = service.price;
      if (this.serviceCaption) this.serviceCaption.textContent = service.caption;

      const variantInput = this.form?.querySelector('[name="variant_id"]');
      const serviceInput = this.form?.querySelector('[name="service_type"]');
      if (variantInput) variantInput.value = service.id;
      if (serviceInput) serviceInput.value = service.title;

      this.drawer.hidden = false;
      document.documentElement.classList.add('career-astrology-drawer-open');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.drawer.querySelector('[data-career-close]')?.focus({ preventScroll: true });
      });
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden || this.isSubmitting) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('career-astrology-drawer-open');
      window.setTimeout(() => {
        this.drawer.hidden = true;
        this.lastTrigger?.focus?.({ preventScroll: true });
      }, 330);
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.classList.remove('is-visible');
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.classList.add('is-visible');
      this.error.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    setLoading(loading) {
      this.isSubmitting = loading;
      if (!this.submitButton) return;
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', String(loading));
      if (this.submitLabel) {
        this.submitLabel.textContent = loading
          ? (this.submitButton.dataset.loadingLabel || 'Preparing secure checkout…')
          : (this.submitButton.dataset.defaultLabel || 'Submit Details & Pay');
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting || !this.form) return;
      this.clearError();

      if (!this.form.reportValidity()) return;

      const data = new FormData(this.form);
      const variantId = Number(data.get('variant_id') || 0);
      if (!variantId) {
        this.showError('The selected service variant could not be found. Please check the product variants in Shopify.');
        return;
      }

      const phone = `${data.get('country_code') || ''} ${data.get('mobile') || ''}`.trim();
      const birthTime = `${data.get('birth_time') || ''} ${data.get('birth_period') || ''}`.trim();
      const properties = {
        'Service Type': data.get('service_type') || '',
        'Full Name': data.get('full_name') || '',
        'Email Address': data.get('email') || '',
        'Mobile Number': phone,
        'Date of Birth': data.get('birth_date') || '',
        'Time of Birth': birthTime,
        'Birth Location': data.get('birth_location') || ''
      };

      const question = String(data.get('career_question') || '').trim();
      if (question) properties['Career Question'] = question;

      this.setLoading(true);

      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }]
          })
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(result.description || result.message || 'This service could not be added. Please try again.');
        }

        window.location.assign(this.dataset.checkoutUrl || '/checkout');
      } catch (error) {
        this.showError(error.message || 'Something went wrong. Please try again.');
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get(SELECTOR)) {
    customElements.define(SELECTOR, CareerAstrologyExperience);
  }
})();
